'use client'

import { dayOptions } from '@/helpers/days'
import { languages } from '@/helpers/languages'
import { trackEvent } from '@/lib/mixpanel'
import PublicSearchForm from './public-search-form'
import { normaliseDraft, pickerOptions } from './search-filter-helpers'

const sortOptions = ['Live', 'Upcoming', 'Finished']
export const liverySortOptions = [
  ['most-recent', 'Most Recent'],
  ['oldest', 'Oldest'],
  ['most-popular', 'Most Popular'],
  ['price-low-to-high', 'Price (low to high)'],
  ['price-high-to-low', 'Price (high to low)'],
  ['name-a-z', 'Name (A-Z)'],
  ['name-z-a', 'Name (Z-A)'],
].map(([value, label]) => ({ value, label }))

export default function DiscoverySearchForm({
  mode = 'events',
  searchParams = {},
  games = [],
  platforms = [],
  action,
  customSearch,
  hidePlatforms = false,
  hideGame = false,
  hideDays = false,
  hideCarClass = true,
  textPlaceholder,
  homepage = false,
}) {
  const livery = mode === 'liveries'
  const casting = mode === 'casting'
  const community = mode === 'communities'
  const fields = []
  if (casting)
    fields.push({
      key: 'type',
      label: 'Role',
      options: pickerOptions([
        { name: 'broadcaster', nicename: 'Broadcaster' },
        { name: 'commentator', nicename: 'Commentator' },
      ]),
    })
  if (!hidePlatforms && !casting)
    fields.push({
      key: livery ? 'platform' : 'platforms',
      label: 'Platform',
      multi: true,
      options: pickerOptions(platforms),
    })
  if (!hideGame)
    fields.push({
      key: 'game',
      label: 'Game',
      searchable: true,
      options: pickerOptions(games, { games: true }),
    })
  if (casting)
    fields.push({
      key: 'language',
      label: 'Language',
      multi: true,
      searchable: true,
      options: pickerOptions(languages),
    })
  if (!hideDays && !livery)
    fields.push({
      key: 'days',
      label: casting ? 'Availability' : 'Race days',
      multi: true,
      options: pickerOptions(dayOptions, { days: true }),
    })
  if (
    (!hideCarClass || (!homepage && mode === 'events')) &&
    !casting &&
    !community
  )
    fields.push({
      key: 'carClasses',
      label: 'Car classes',
      multi: true,
      searchable: 'long',
      gameCars: true,
      advanced: !livery,
      options: pickerOptions(
        games.flatMap((game) => game.carClasses || []),
        { labelKey: 'name' },
      ),
    })
  fields.push({ key: 'keyword', kind: 'text', label: 'Keyword', maxLength: 80 })
  if (!homepage && mode === 'events')
    fields.push(
      {
        key: 'type',
        label: 'Race type',
        advanced: true,
        options: pickerOptions(['Single Event', 'Championship Event']),
      },
      {
        key: 'sort',
        label: 'Sort by',
        advanced: true,
        options: pickerOptions(sortOptions),
      },
    )
  if (community)
    fields.push({
      key: 'communityType',
      label: 'Community type',
      advanced: true,
      options: pickerOptions(['Managed', 'Listed']),
    })
  if (livery)
    fields.push({
      key: 'orderBy',
      label: 'Sort by',
      options: liverySortOptions,
      defaultValue: 'most-popular',
      advanced: true,
    })
  const raw = { ...searchParams }
  if (livery && !raw.platform && raw.platforms) raw.platform = raw.platforms
  // Match the spellings accepted by the existing server URL adapters.
  if (mode === 'events') {
    raw.sort =
      { live: 'Live', upcoming: 'Upcoming', finished: 'Finished' }[
        String(raw.sort || '').toLowerCase()
      ] || null
    raw.type =
      {
        single: 'Single Event',
        'single event': 'Single Event',
        championship: 'Championship Event',
        'championship event': 'Championship Event',
      }[String(raw.type || '').toLowerCase()] || null
  }
  if (community)
    raw.communityType =
      { managed: 'Managed', listed: 'Listed' }[
        String(raw.communityType || '').toLowerCase()
      ] || null
  const applied = normaliseDraft(fields, raw)
  const encode = (draft) => {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(draft)) {
      if (Array.isArray(value) ? value.length : value)
        params.set(key, Array.isArray(value) ? value.join(',') : value)
    }
    params.set('page', '1')
    if (community) params.set('pageSize', '24')
    if (homepage || casting || community) params.set('ts', String(Date.now()))
    return params
  }
  return (
    <PublicSearchForm
      fields={fields}
      layout={homepage ? 'homepage' : 'default'}
      showClearAll={!homepage}
      showUnappliedChanges={!homepage}
      showUpdatingResults={!homepage}
      navigateImmediately={homepage}
      applied={applied}
      action={action}
      encode={encode}
      games={games}
      platformKey={livery ? 'platform' : 'platforms'}
      keywordPlaceholder={
        textPlaceholder ||
        (casting
          ? 'Search name, bio...'
          : community
            ? 'Search communities...'
            : 'Search communities, tracks etc...')
      }
      advancedId={
        mode === 'events'
          ? 'advanced-event-filters'
          : community
            ? 'advanced-community-filters'
            : undefined
      }
      onSubmit={customSearch}
      onTrack={
        homepage || livery
          ? (params) =>
              trackEvent(livery ? 'Livery Search' : 'Event Search', params)
          : undefined
      }
    />
  )
}
